/**
 * ApprovalsQueueView — pending Schedule A changes awaiting THIS user's approval.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Check, X } from 'lucide-react';
import api from '../../lib/api';

export default function ApprovalsQueueView() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const d = await api.getScheduleAPendingApprovals();
      setPending(d || []);
    } catch (e) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (id, approve) => {
    try {
      if (approve) await api.approveScheduleA(id);
      else await api.rejectScheduleA(id);
      toast.success(approve ? 'Approved' : 'Rejected');
      load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <Card data-testid="approvals-view">
      <CardHeader>
        <CardTitle>Pending Approvals ({pending.length})</CardTitle>
        <p className="text-xs text-muted-foreground">
          Compensation changes proposed by your direct downlines that need your approval.
        </p>
      </CardHeader>
      <CardContent>
        {pending.length === 0 && (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="approvals-empty">
            No pending compensation changes to review.
          </p>
        )}
        <div className="space-y-2">
          {pending.map((p) => (
            <div key={p.id} className="p-3 border rounded-lg" data-testid={`approval-${p.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm">
                    <span className="font-medium">{p.parent_name}</span>
                    <span className="text-muted-foreground"> proposed for </span>
                    <span className="font-medium">{p.child_name}</span>
                    <span className="text-xs text-muted-foreground ml-2">({p.child_role?.replace('_', ' ')})</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Effective {p.effective_date} • v{p.version}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => decide(p.id, false)} data-testid={`reject-${p.id}`}>
                    <X className="h-3 w-3 mr-1" /> Reject
                  </Button>
                  <Button size="sm" onClick={() => decide(p.id, true)} data-testid={`approve-${p.id}`}>
                    <Check className="h-3 w-3 mr-1" /> Approve
                  </Button>
                </div>
              </div>
              <div className="text-sm bg-muted/40 rounded p-2">
                {p.schedule_a?.compensation_type === 'fixed' ? (
                  <span>
                    <Badge variant="secondary" className="mr-2">Fixed</Badge>
                    {p.schedule_a.split_percent}% of parent&apos;s residual
                  </span>
                ) : (
                  <div>
                    <Badge variant="secondary" className="mr-2">Tiered</Badge>
                    Volume method: {p.schedule_a?.tier_volume_method || 'CURRENT_MONTH'}
                    <table className="w-full mt-2 text-xs">
                      <tbody>
                        {(p.schedule_a?.tiers || []).map((t, i) => (
                          <tr key={i}>
                            <td className="py-1">
                              ${Number(t.min_volume).toLocaleString()} – {t.max_volume ? `$${Number(t.max_volume).toLocaleString()}` : '∞'}
                            </td>
                            <td className="py-1 text-right font-medium">{t.split_percent}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {p.note && <p className="text-xs text-muted-foreground mt-2 italic">"{p.note}"</p>}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
